import { Fragment, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  IconButton,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  Search,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ClearAll,
  Close,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { QuoteHistoryItem, formatQuoteDate } from "../../components/features/rfq/quoteHistory";
import { useQuoteHistory } from "../../components/features/rfq/useQuoteHistory";
import { Page, PageHeader } from "../../components/layout/Page";

const money = (n: number | null | undefined) =>
  `$${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TIGHT = { width: "1%", whiteSpace: "nowrap" } as const;
const visuallyHidden = { position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" } as const;
// expand, RFQ, Sent, Customer, Items, Total
const RESULT_COLS = 6;

// One quote as a table row; clicking it (or its chevron) shows the quoted items beneath.
function QuoteRow({ item }: { item: QuoteHistoryItem }) {
  const [open, setOpen] = useState(false);
  const formattedDate = formatQuoteDate(item);
  const toggle = () => setOpen((o) => !o);

  return (
    <Fragment>
      <TableRow
        hover
        onClick={toggle}
        selected={open}
        sx={{ cursor: "pointer", "& > td": open ? { borderBottom: 0 } : {} }}
      >
        <TableCell sx={{ ...TIGHT, pr: 0 }}>
          <IconButton
            size="small"
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} items for RFQ #${item.rfq_id}`}
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
          >
            {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={TIGHT}>
          <Typography variant="body2" fontWeight={600}>
            RFQ #{item.rfq_id}
          </Typography>
        </TableCell>
        <TableCell sx={{ ...TIGHT, color: "text.secondary" }}>{formattedDate}</TableCell>
        <TableCell sx={{ minWidth: 220 }}>
          <Typography variant="body2" fontWeight={500}>
            {item.customer_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: "break-all" }}>
            {item.customer_email}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={TIGHT}>
          {item.items_count}
        </TableCell>
        <TableCell align="right" sx={{ ...TIGHT, fontWeight: 600 }}>
          {money(item.total_amount)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={RESULT_COLS} sx={{ p: 0, ...(open ? {} : { borderBottom: 0 }) }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, pt: 0.5, pl: { sm: 7 }, bgcolor: "action.hover" }}>
              <Table size="small" sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Model Number</TableCell>
                    <TableCell align="right" sx={TIGHT}>Quantity</TableCell>
                    <TableCell align="right" sx={TIGHT}>Unit Price</TableCell>
                    <TableCell align="right" sx={TIGHT}>Total Price</TableCell>
                    <TableCell align="right" sx={TIGHT}>Indent Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(item.items ?? [])
                    .slice()
                    .sort((a, b) => (a.order_index ?? a.rfq_item_id) - (b.order_index ?? b.rfq_item_id))
                    .map((rfqItem) => (
                    <TableRow key={rfqItem.rfq_item_id}>
                      <TableCell sx={{ minWidth: 200 }}>{rfqItem.item_name}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{rfqItem.model_number}</TableCell>
                      <TableCell align="right" sx={TIGHT}>{rfqItem.quantity}</TableCell>
                      <TableCell align="right" sx={TIGHT}>{money(rfqItem.quoted_price)}</TableCell>
                      <TableCell align="right" sx={TIGHT}>{money(rfqItem.total_price)}</TableCell>
                      <TableCell align="right" sx={TIGHT}>{rfqItem.indent_days}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

// Main Quote History Page Component
export default function QuoteHistory() {
  const {
    customerEmail, setCustomerEmail,
    rfqId, setRfqId,
    startDate, setStartDate,
    endDate, setEndDate,
    keyword, setKeyword,
    clearFilters,
    data, isLoading, error, refetch,
    quotes,
  } = useQuoteHistory({
    // Oldest first. This page used to sort newest-first and then group by
    // quote_group_id (= the RFQ id) into an object, whose integer-like keys
    // JavaScript iterates in ascending order - so ascending is what it has
    // always shown. Kept as-is in this refactor.
    order: "asc",
    query: { staleTime: 30000 }, // Cache results for 30 seconds
  });

  return (
    <Page width="full">
      <Box>
        <PageHeader
          title="Quote History"
          actions={!isLoading && data?.history && (
            <Typography variant="subtitle1" color="text.secondary">
              {data.history.length} {data.history.length === 1 ? 'Quote' : 'Quotes'} Found
            </Typography>
          )}
        />

        {/* Filter Section - Always visible. Fields wrap onto extra lines on narrow windows. */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              label="Search Items"
              placeholder="Search by item name or model number"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              sx={{ flex: "2 1 280px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: keyword && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setKeyword("")}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              label="Customer Email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              sx={{ flex: "1 1 220px" }}
            />
            <TextField
              size="small"
              label="RFQ ID"
              type="number"
              value={rfqId}
              onChange={(e) => setRfqId(e.target.value ? Number(e.target.value) : "")}
              sx={{ flex: "0 1 140px", minWidth: 120 }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                slotProps={{ textField: { size: "small", sx: { flex: "0 1 170px", minWidth: 150 } } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                slotProps={{ textField: { size: "small", sx: { flex: "0 1 170px", minWidth: 150 } } }}
              />
            </LocalizationProvider>
            <Box sx={{ display: "flex", gap: 1, flex: "0 0 auto" }}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={() => refetch()}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearAll />}
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Loading and Error States */}
        {isLoading && (
          <Paper sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading quote history. Please try again.
          </Alert>
        )}

        {/* Results Section: one row per quote, expandable to its items */}
        {!isLoading && !error && data?.history && (
          data.history.length === 0 ? (
            <Alert severity="info">
              No quote history found. Try adjusting your filters.
            </Alert>
          ) : (
            <Paper sx={{ overflow: "hidden" }}>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...TIGHT, pr: 0 }}>
                        <Box component="span" sx={visuallyHidden}>Expand</Box>
                      </TableCell>
                      <TableCell sx={TIGHT}>RFQ</TableCell>
                      <TableCell sx={TIGHT}>Sent</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right" sx={TIGHT}>Items</TableCell>
                      <TableCell align="right" sx={TIGHT}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quotes.map((item) => (
                      <QuoteRow key={item.rfq_id} item={item} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )
        )}
      </Box>
    </Page>
  );
}
